import { cn } from '@recipes/ui';
import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Plain-text label used on the stacked mobile layout (defaults to header when string). */
  label?: string;
  className?: string;
}

/**
 * Accessible data table that renders as a regular <table> on md+ screens and as a
 * stack of cards on phones (each cell prefixed with its column label).
 */
export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  caption,
  empty,
  className,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption: string;
  empty?: ReactNode;
  className?: string;
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        {empty ?? '–'}
      </p>
    );
  }
  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-separate border-spacing-0 text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="hidden md:table-header-group">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn(
                  'border-b bg-muted/50 px-3 py-2 text-left font-medium text-muted-foreground',
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="block space-y-3 md:table-row-group md:space-y-0">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="block rounded-lg border p-3 md:table-row md:rounded-none md:border-0 md:p-0"
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  data-label={c.label ?? (typeof c.header === 'string' ? c.header : c.key)}
                  className={cn(
                    'flex items-start justify-between gap-3 py-1 align-top before:shrink-0 before:text-xs before:font-medium before:text-muted-foreground before:content-[attr(data-label)] md:table-cell md:border-b md:px-3 md:py-2 md:before:content-none',
                    c.className,
                  )}
                >
                  <span className="min-w-0 text-right md:text-left md:contents">{c.cell(row)}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
